import { html, svg } from 'lit-element';
import { nothing } from 'lit-html';
import { nextUniqueId } from '../../shared/base/unique-id';
import { KatalComponent, KatLitElement } from '../../shared/base';
import { getFirstMatchingParent } from '../../shared/utils';
import { roundStars } from './star-rating-calculator';

const FULL_STAR = 'full-star';
const HALF_STAR = 'half-star';
const EMPTY_STAR = 'empty-star';

/**
 * @component {kat-star-rating} Star Rating is a common Amazon affordance that allows customers to rate their experience.
 * @guideline Do Star ratings can be filled in half star or full star increments.
 * @guideline Dont Do not use gradients or change the fill colors
 * @guideline Do Do use the input variant to allow users to select a rating.
 * @guideline Do Do use the default variant for to display a rating.
 * @guideline Dont StarRating and StarRatingWithLink variants are to be used for display purposes only. StarRatingInput and StarRatingInputWithError are to be used only as inputs (not for display)
 * @example StarRating {"rating": "4.5","review-label":"236"}
 * @example StarRatingWithLink {"rating": "2","review-label":"236","href":"https://www.amazon.com"}
 * @example StarRatingInput {"variant": "input"}
 * @example StarRatingInputWithError {"rating": "2","variant": "input", "error-label": "Please choose a rating"}
 * @event change Fires when the user selects a different rating.
 * @status Production
 * @theme flo
 */
export class KatalStarRating extends KatLitElement {
  /**
   * @deprecated
   * These will both be removed in 1.3 in favor of requiring these values to
   * be set as numbers.
   */
  _rating = 0;
  _stars = 5;

  _uniqueGradientId = nextUniqueId();

  static get properties() {
    return {
      /**
       * @classprop {number} rating This is the score that will be displayed as full stars and half-stars out of the total number of stars
       * @required
       */
      rating: { type: Number, reflect: true },

      /**
       * @classprop {string} review-label This is the review label
       */
      reviewLabel: { type: String, attribute: 'review-label', reflect: true },

      /**
       * @classprop {string} error-label This is the text to show if there is an error.
       */
      errorLabel: { type: String, attribute: 'error-label', reflect: true },

      /**
       * @classprop {string} href This will make the star rating component a clickable link
       */
      href: { type: String, reflect: true },

      /**
       * @classprop {number} stars The number of stars to display. Defaults to 5.
       */
      stars: { type: Number, reflect: true },

      /**
       * @classprop {enum} variant Defines if the component can be used to display or set a rating.
       * @enum {value} default Component is display only.
       * @enum {value} input Component is used to select star rating.
       */
      variant: { type: String, reflect: true },

      /**
       * @classprop {string} name The name of this as an input inside a form.
       */
      name: { type: String, reflect: true },
    };
  }

  // disable shadow-dom
  createRenderRoot() {
    return this;
  }

  get rating() {
    return this._rating;
  }

  set rating(value) {
    const oldValue = this._rating;
    this._rating = typeof value === 'number' ? value : parseFloat(value);
    if (isNaN(this._rating)) {
      this._rating = 0;
    }
    this.requestUpdate('rating', oldValue);
  }

  get stars() {
    return this._stars;
  }

  set stars(value) {
    const oldValue = this._stars;
    this._stars = typeof value === 'number' ? value : parseFloat(value);
    if (isNaN(this._stars)) {
      this._stars = 5;
    }
    this.requestUpdate('stars', oldValue);
  }

  firstUpdated() {
    this.addEventListener('click', evt => {
      if (this.variant !== 'input') {
        return;
      }

      const rating = this._getRatingFromStarEvent(evt);
      if (rating != null && rating !== this.rating) {
        this.rating = rating;
        this.dispatchCustomEvent('change', { rating });
      }
    });
  }

  render() {
    const starsElement = this._createStarsElement();
    const review = html`
      <span class="review">${this.reviewLabel}</span>
    `;

    const firstLine = (() => {
      const getFirstLine = children =>
        html`
          <span>${children}</span>
        `;
      const getLink = children =>
        html`
          <a href="${this.href}" target="_blank">${children}</a>
        `;

      if (this.href) {
        if (this.variant === 'input') {
          return getFirstLine([starsElement, getLink(review)]);
        }
        return getLink([starsElement, review]);
      }
      return getFirstLine([starsElement, review]);
    })();

    const error = this.errorLabel
      ? html`
          <kat-label
            class="error-label"
            variant="constraint"
            text="${this.errorLabel}"
            state="error"
          ></kat-label>
        `
      : nothing;

    const hiddenInput = this.name
      ? html`
          <input type="hidden" name="${this.name}" value=${this.rating} />
        `
      : nothing;

    return html`
      ${firstLine}${error}${hiddenInput}
    `;
  }

  _createStarsElement() {
    const numStars = this.stars;
    const [fullStarCount, hasHalfStar] = roundStars(this.rating);

    const icons = [];
    for (let i = 0; i < numStars; ++i) {
      let icon;
      if (i < fullStarCount) {
        icon = this._createStar(FULL_STAR);
      } else if (hasHalfStar && i === fullStarCount) {
        icon = this._createStar(HALF_STAR);
      } else {
        icon = this._createStar(EMPTY_STAR);
      }
      icons.push(icon);
    }

    return html`
      <span class="stars">${icons}</span>
    `;
  }

  _createGradientDefinition() {
    return svg`
      <defs>
        <linearGradient
          x1="50%"
          y1="0%"
          x2="50%"
          y2="100%"
          id="${this._uniqueGradientId}"
        >
          <stop offset="0%" stop-color="#FED007" />
          <stop offset="100%" stop-color="#FEB502" />
        </linearGradient>
      </defs>
    `;
  }

  _createStar(type) {
    const gradientFill = `url(#${this._uniqueGradientId})`;
    const halfPolygon = svg`
      <polygon
        points="12,13.8 8.6,15.7 9.3,11.9 6.6,9.2 10.3,8.7 12,5.3"
        fill="${gradientFill}"
      />
    `;

    const children = [
      type === EMPTY_STAR ? nothing : this._createGradientDefinition(),

      svg`
        <g pointer-events="bounding-box">
          <path
            d="M16,16.6l-0.8-4.5l3.3-3.2L14,8.2l-2-4.1l-2,4.1L5.5,8.9l3.3,3.2L8,16.6l4-2.1L16,16.6z"
            stroke="#C88702"
            fill="${type === FULL_STAR ? gradientFill : '#FFFFFF'}"
          />
          ${type === HALF_STAR ? halfPolygon : nothing}
        </g>
      `,
    ];

    return svg`
      <svg viewBox="5 4 14 14" class="${`katal-starrating-${type}`}">
        ${children}
      </svg>
    `;
  }

  _getRatingFromStarEvent(evt) {
    const icon = getFirstMatchingParent(
      evt.target,
      el => el.tagName.toLowerCase() === 'svg',
      this
    );
    if (!icon) {
      return null;
    }

    const children = icon.parentNode.children;
    for (let i = 0; i < children.length; i++) {
      if (icon === children[i]) {
        return i + 1;
      }
    }

    return null;
  }
}

KatalComponent.register('kat-star-rating', KatalStarRating);
